import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Loader2, X } from "lucide-react";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import { fmtOdd } from "../../utils/bettingDeskHelpers";
import { projectStakeEdit } from "../../utils/joinedBetsHelpers";

const JoinedBetEditModal = ({
  open,
  bet,
  userCash,
  busy,
  onClose,
  onSubmit,
}) => {
  const [amount, setAmount] = React.useState("");

  useEffect(() => {
    if (open) setAmount("");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, busy, onClose]);

  const numeric = Number(String(amount).replace(/,/g, ""));
  const projection = useMemo(
    () => projectStakeEdit(bet, numeric),
    [bet, numeric]
  );

  if (!open) return null;

  const insufficient = projection.hasAdditional && numeric > Number(userCash || 0);
  const invalid =
    !projection.hasAdditional || !Number.isFinite(numeric) || numeric <= 0 || insufficient;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (invalid || busy) return;
    onSubmit(numeric);
  };

  return createPortal(
    <div className="jb-modal-root" role="dialog" aria-modal="true">
      <button
        type="button"
        className="jb-modal-backdrop"
        aria-label="Close"
        disabled={busy}
        onClick={onClose}
      />
      <form className="jb-modal-sheet jb-modal-sheet-stake" onSubmit={handleSubmit}>
        <button
          type="button"
          className="jb-modal-close"
          aria-label="Close"
          disabled={busy}
          onClick={onClose}
        >
          <X size={16} />
        </button>
        <p className="jb-modal-eyebrow">Add stake</p>
        <h2 className="jb-modal-title">Increase your ticket</h2>
        <p className="jb-modal-sub">
          Enter how much more Cash to add. Your total stake and potential return update
          instantly below.
        </p>

        <label className="jb-modal-field-label" htmlFor="jb-stake-add-input">
          Additional amount
        </label>
        <input
          id="jb-stake-add-input"
          type="number"
          min="0"
          step="any"
          className="jb-modal-input jb-modal-input-lg tabular-nums"
          placeholder="0"
          value={amount}
          disabled={busy}
          onChange={(e) => setAmount(e.target.value)}
          autoFocus
        />

        <div className="jb-modal-preview" aria-live="polite">
          <div className="jb-modal-preview-head">
            <span>Before you confirm</span>
            <em className="tabular-nums">× {fmtOdd(projection.multiplier)}</em>
          </div>

          <div className="jb-modal-preview-row">
            <span>Total stake</span>
            <div className="jb-modal-preview-values">
              <strong className="tabular-nums">
                {formatCash(projection.currentStake, { compact: false })}
              </strong>
              {projection.hasAdditional ? (
                <>
                  <ArrowRight size={14} aria-hidden />
                  <strong className="tabular-nums jb-modal-preview-new">
                    {formatCash(projection.newStake, { compact: false })}
                  </strong>
                </>
              ) : null}
            </div>
          </div>

          <div className="jb-modal-preview-row jb-modal-preview-row-accent">
            <span>Potential return</span>
            <div className="jb-modal-preview-values">
              <strong className="tabular-nums">
                {formatCash(projection.currentPotential, { compact: false })}
              </strong>
              {projection.hasAdditional ? (
                <>
                  <ArrowRight size={14} aria-hidden />
                  <strong className="tabular-nums jb-modal-preview-new">
                    {formatCash(projection.newPotential, { compact: false })}
                  </strong>
                </>
              ) : null}
            </div>
          </div>

          {projection.hasAdditional ? (
            <p className="jb-modal-preview-note tabular-nums">
              +{formatCash(projection.additional, { compact: false })} added to your stake
            </p>
          ) : (
            <p className="jb-modal-preview-note jb-modal-preview-note-muted">
              Enter an amount to preview your new total stake and return.
            </p>
          )}
        </div>

        {insufficient ? (
          <p className="jb-modal-error">
            Insufficient Cash — wallet has {formatCash(userCash || 0, { compact: true })}.
          </p>
        ) : null}

        <div className="jb-modal-actions">
          <button
            type="button"
            className="jb-modal-cancel"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          <button type="submit" className="jb-modal-confirm" disabled={busy || invalid}>
            {busy ? (
              <Loader2 size={14} className="animate-spin" aria-label="Updating" />
            ) : (
              "Confirm add stake"
            )}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default JoinedBetEditModal;
